import sortBy from 'lodash/sortBy';
import { getRotatePosition } from '@/utils/collision-kit';
import { convertLayerInfoToKeyAxis, convertLayerInfoToPoints, getLayerInfoWithRotate } from '@/utils/layer-kit';
import { REFERENCE_LINE_DIRECTION, type ILayerInfo, type ITransform, type ReferenceLine } from '@/version-types';

function getRotatedPoints(layer: ITransform, layerRect: ILayerInfo, direction?: 'v' | 'h') {
    const points = getRotatePosition(layer);

    const { left, top, width, height, id } = layerRect;
    const centerX = Math.round(width / 2);
    const centerY = Math.round(height / 2);
    if (layer.isStage) {
        if (direction === 'v') {
            points.push({
                id,
                x: centerX,
                y: 0,
            });
            points.push({
                id,
                x: centerX,
                y: height,
            });
        } else if (direction === 'h') {
            points.push({
                id,
                x: 0,
                y: centerY,
            });
            points.push({
                id,
                x: width,
                y: centerY,
            });
        }
    } else {
        // 加入中心点
        points.push({
            id,
            x: left + centerX,
            y: top + centerY,
        });
    }

    return points;
}

type NearestLine = {
    id?: string;
    distance: number;
    position: number;
};

class LineList {
    private data: ILayerInfo[];
    private layerMap: Record<string, ITransform>;
    private referenceLines: ReferenceLine[];

    constructor() {
        this.data = [];
        this.layerMap = {};
        this.referenceLines = [];
    }

    get length() {
        return this.data.length;
    }

    /**
     * 把参考线加入 guide 的计算列表中
     *
     * @param referenceLines - 参考线
     * @param width - 舞台宽度
     * @param height - 舞台高度
     */
    addReferenceLineData(referenceLines: ReferenceLine[]) {
        this.referenceLines = referenceLines;
    }

    push(layer: ITransform) {
        const info = getLayerInfoWithRotate(layer);
        this.data.push(info);
        this.layerMap[info.id] = layer;
    }

    getNearest(layer: ITransform) {
        const layerRect = getLayerInfoWithRotate(layer);
        const infoA = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        // 参考线位置要在 layer 前面,因为参考线高亮的优先级大于其他图层
        this.referenceLines.forEach((referenceLine) => {
            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                [infoA.lt, infoA.tc, infoA.rt].forEach((a, index) => {
                    vertical.push({
                        // 2 条线的间隔
                        distance: Math.floor(a.x - referenceLine.position),
                        // 目标线的 x 坐标
                        position: Math.floor(
                            referenceLine.position - ~~(index === 0 ? 0 : layerRect.width / (3 - index)),
                        ),
                    });
                });
            }

            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                [infoA.lt, infoA.lc, infoA.lb].forEach((a, index) => {
                    horizontal.push({
                        // 2 条线的间隔
                        distance: Math.floor(a.y - referenceLine.position),
                        // 目标线的 y 坐标
                        position: Math.floor(
                            referenceLine.position - ~~(index === 0 ? 0 : layerRect.height / (3 - index)),
                        ),
                    });
                });
            }
        });

        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);
            [infoA.lt, infoA.tc, infoA.rt].forEach((a, index) => {
                [infoB.lt, infoB.tc, infoB.rt].forEach((b) => {
                    vertical.push({
                        id: item.id,
                        // 2 条线的间隔
                        distance: Math.floor(a.x - b.x),
                        // 目标线的 x 坐标
                        position: Math.floor(b.x - ~~(index === 0 ? 0 : layerRect.width / (3 - index))),
                    });
                });
            });
            [infoA.lt, infoA.lc, infoA.lb].forEach((a, index) => {
                [infoB.lt, infoB.lc, infoB.lb].forEach((b) => {
                    horizontal.push({
                        id: item.id,
                        // 2 条线的间隔
                        distance: Math.floor(a.y - b.y),
                        // 目标线的 y 坐标
                        position: Math.floor(b.y - ~~(index === 0 ? 0 : layerRect.height / (3 - index))),
                    });
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getNearestLineForResize(layer: ITransform, directionVector: { x: number; y: number }) {
        const layerRect = getLayerInfoWithRotate(layer);
        const layerPoints = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        const { x: directionX, y: directionY } = directionVector;
        const xPoint = directionX === 0 ? Infinity : directionX > 0 ? layerPoints.rt.x : layerPoints.lt.x;
        const yPoint = directionY === 0 ? Infinity : directionY > 0 ? layerPoints.rb.y : layerPoints.rt.y;
        // 参考线位置要在 layer 前面,因为参考线高亮的优先级大于其他图层
        this.referenceLines.forEach((referenceLine) => {
            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                vertical.push({
                    // 2 条线的间隔
                    distance: xPoint - referenceLine.position,
                    // 目标线的 x 坐标
                    position: referenceLine.position,
                });
            }

            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                horizontal.push({
                    // 2 条线的间隔
                    distance: yPoint - referenceLine.position,
                    // 目标线的 y 坐标
                    position: referenceLine.position,
                });
            }
        });

        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);

            // resize 的时候可以不用考虑中心点
            // [infoB.lt, infoB.tc, infoB.rt]
            [infoB.lt, infoB.rt].forEach((b) => {
                vertical.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: xPoint - b.x,
                    // 目标线的 x 坐标
                    position: b.x,
                });
            });
            // [infoB.lt, infoB.lc, infoB.lb]
            [infoB.lt, infoB.lb].forEach((b) => {
                horizontal.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: yPoint - b.y,
                    // 目标线的 y 坐标
                    position: b.y,
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getNearestLineForScale(layer: ITransform, directionVector: { x: number; y: number }) {
        const layerRect = getLayerInfoWithRotate(layer);
        const layerPoints = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        const { x: directionX, y: directionY } = directionVector;
        const xPoint = directionX === 0 ? Infinity : directionX > 0 ? layerPoints.rt.x : layerPoints.lt.x;
        const yPoint = directionY === 0 ? Infinity : directionY > 0 ? layerPoints.rb.y : layerPoints.rt.y;
        // 参考线位置要在 layer 前面,因为参考线高亮的优先级大于其他图层
        this.referenceLines.forEach((referenceLine) => {
            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                vertical.push({
                    // 2 条线的间隔
                    distance: xPoint - referenceLine.position,
                    // 目标线的 x 坐标
                    position: referenceLine.position,
                });
            }

            if (referenceLine.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                horizontal.push({
                    // 2 条线的间隔
                    distance: yPoint - referenceLine.position,
                    // 目标线的 y 坐标
                    position: referenceLine.position,
                });
            }
        });

        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);

            // resize 的时候可以不用考虑中心点
            // [infoB.lt, infoB.tc, infoB.rt]
            [infoB.lt, infoB.rt].forEach((b) => {
                vertical.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: xPoint - b.x,
                    // 目标线的 x 坐标
                    position: b.x,
                });
            });
            // [infoB.lt, infoB.lc, infoB.lb]
            [infoB.lt, infoB.lb].forEach((b) => {
                horizontal.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: yPoint - b.y,
                    // 目标线的 y 坐标
                    position: b.y,
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getLines(currentLayer: ITransform, ignoreCenter: boolean = false) {
        const currentLayerRect = getLayerInfoWithRotate(currentLayer);
        const currentLayerPoints = convertLayerInfoToKeyAxis(currentLayerRect, ignoreCenter);
        const currentLayerRotatedPoints = getRotatedPoints(currentLayer, currentLayerRect);

        const res: {
            id: string;
            isHorizontal: boolean;
            points: { x: number; y: number }[];
        }[] = [];

        this.data.forEach((layerRect) => {
            const layerPoints = convertLayerInfoToKeyAxis(layerRect, ignoreCenter);
            layerPoints.x.forEach((x) => {
                if (currentLayerPoints.x.includes(x)) {
                    const layer = this.layerMap[layerRect.id];
                    const layerRotatedPoints = getRotatedPoints(layer, layerRect, 'v');
                    const points = [...layerRotatedPoints, ...currentLayerRotatedPoints]
                        .filter((p) => Math.abs(p.x - x) < 1.5)
                        .sort((a, b) => a.y - b.y);
                    res.push({
                        id: layerRect.id,
                        isHorizontal: false,
                        points: points.map((p) => ({ x, y: p.y })),
                    });
                }
            });
            layerPoints.y.forEach((y) => {
                if (currentLayerPoints.y.includes(y)) {
                    const layer = this.layerMap[layerRect.id];
                    const layerRotatedPoints = getRotatedPoints(layer, layerRect, 'h');
                    const points = [...layerRotatedPoints, ...currentLayerRotatedPoints]
                        .filter((p) => Math.abs(p.y - y) < 1.5)
                        .sort((a, b) => a.x - b.x);
                    res.push({
                        id: layerRect.id,
                        isHorizontal: true,
                        points: points.map((p) => ({ x: p.x, y })),
                    });
                }
            });
        });
        return res;
    }

    getReferenceLines(layer: ITransform) {
        const rect = getLayerInfoWithRotate(layer);
        const { left, top, width, height } = rect;
        const right = left + width;
        const bottom = top + height;
        const halfWidth = ~~(width / 2);
        const halfHeight = ~~(height / 2);
        const centerX = left + halfWidth;
        const centerY = top + halfHeight;

        const res: string[] = [];

        const tolerance = 1;
        this.referenceLines.forEach(({ id, direction, position }) => {
            // 垂直线
            if (direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                if (
                    Math.abs(left - position) < tolerance ||
                    Math.abs(centerX - position) < tolerance ||
                    Math.abs(right - position) < tolerance
                ) {
                    res.push(id);
                }
            }

            // 水平线
            if (direction === REFERENCE_LINE_DIRECTION.HORIZONTAL) {
                if (
                    Math.abs(top - position) < tolerance ||
                    Math.abs(centerY - position) < tolerance ||
                    Math.abs(bottom - position) < tolerance
                ) {
                    res.push(id);
                }
            }
        });

        return res;
    }
}

export default LineList;
